import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Heart, Share2, Star, Clock, Plus, Minus, Trash2, MapPin, Phone, ShoppingCart, Bike, Store, Images } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { money, OCASIONES } from '../lib/constants';
import { Screen, TopBar, PrimaryButton } from './PhoneFrame';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { DeliveryFeeBreakdown } from '../components/DeliveryFeeBreakdown';
import { deliveryPrice, selectedAddress, roundMoney } from '../lib/deliveryPricing';

const REVIEWS = [
  { name: 'María López', rating: 5, text: 'Excelente sabor y llegó muy rápido. ¡Recomendado!', time: 'Hace 2 días' },
  { name: 'Luis Gómez', rating: 4, text: 'Muy bueno, el combo familiar alcanza para todos.', time: 'Hace 5 días' },
  { name: 'Ana Martínez', rating: 5, text: 'Siempre pido aquí, nunca falla.', time: 'Hace 1 semana' },
];

// Swipeable product gallery (scroll-snap) with dots; falls back to a single photo.
const ProductGallery = ({ images, alt }) => {
  const [idx, setIdx] = useState(0);
  const onScroll = (e) => {
    const w = e.currentTarget.clientWidth || 1;
    setIdx(Math.round(e.currentTarget.scrollLeft / w));
  };
  if (images.length <= 1) return <img src={images[0]} alt={alt} className="w-full h-40 rounded-2xl object-cover" data-testid="product-photo" />;
  return (
    <div className="relative" data-testid="product-gallery">
      <div onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar rounded-2xl">
        {images.map((url, i) => (
          <img key={url} src={url} alt={alt} data-testid={`product-photo-${i}`} className="w-full h-40 shrink-0 snap-center object-cover" />
        ))}
      </div>
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5" data-testid="product-gallery-dots">
        {images.map((url, i) => <span key={url} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/60'} shadow`} />)}
      </div>
      <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/55 text-white text-[10px] font-bold">{idx + 1}/{images.length}</span>
    </div>
  );
};

// Product sheet: choose flavour / size (drinks) and quantity before adding.
const ProductSheet = ({ product, onAdd }) => {
  const flavors = product.options || [];
  const sizes = product.sizes || [];
  const gallery = (product.images && product.images.length ? product.images : [product.image]).filter(Boolean);
  const [flavor, setFlavor] = useState(flavors[0] || null);
  const [size, setSize] = useState(sizes[0] || null);
  const [qty, setQty] = useState(1);
  const unit = Number(product.price) + Number(flavor?.price || 0) + Number(size?.price || 0);
  const variant = [flavor?.name, size?.name].filter(Boolean).join(' · ');

  const submit = () => {
    if (flavors.length && !flavor) return toast.error('Elige un sabor');
    onAdd({
      id: variant ? `${product.id}|${[flavor?.id, size?.id].filter(Boolean).join('-')}` : product.id,
      name: variant ? `${product.name} (${variant})` : product.name,
      price: unit,
      image: product.image,
      qty,
      variant: variant || undefined,
    });
  };

  return (
    <div className="p-4 pb-8" data-testid="product-sheet">
      <ProductGallery images={gallery} alt={product.name} />
      <h3 className="text-xl font-extrabold mt-3">{product.name}</h3>
      {product.desc && <p className="text-sm text-gray-500">{product.desc}</p>}

      {flavors.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Sabor</p>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="flavor-options">
            {flavors.map((f) => (
              <button key={f.id} data-testid={`flavor-${f.id}`} onClick={() => setFlavor(f)} className={`h-10 px-3.5 rounded-xl text-[13px] font-bold border press ${flavor?.id === f.id ? 'bg-[#EE3B36] border-[#EE3B36] text-white' : 'bg-white border-gray-200 text-gray-700'}`}>{f.name}{f.price ? ` +${money(f.price)}` : ''}</button>
            ))}
          </div>
        </div>
      )}

      {sizes.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Tamaño</p>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="size-options">
            {sizes.map((s) => (
              <button key={s.id} data-testid={`size-${s.id}`} onClick={() => setSize(s)} className={`h-10 px-3.5 rounded-xl text-[13px] font-bold border press ${size?.id === s.id ? 'bg-[#1E2130] border-[#1E2130] text-white' : 'bg-white border-gray-200 text-gray-700'}`}>{s.name}{s.price ? ` +${money(s.price)}` : ''}</button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-5">
        <div className="flex items-center gap-3 bg-[#F7F8FA] rounded-xl p-1">
          <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center press"><Minus size={16} /></button>
          <span data-testid="qty-value" className="w-5 text-center font-extrabold">{qty}</span>
          <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center press"><Plus size={16} /></button>
        </div>
        <span data-testid="product-total" className="text-2xl font-extrabold">{money(unit * qty)}</span>
      </div>
      <PrimaryButton className="mt-4" data-testid="sheet-add-to-cart" onClick={submit}><ShoppingCart size={16} className="inline mr-2" /> Agregar al carrito</PrimaryButton>
    </div>
  );
};

export const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { restaurants, menus, favorites, toggleFavorite, addToCart, cart, cartCount, cartSubtotal, checkout, setCheckout, addresses, coverage, deliveryPricing, pricingReady } = useApp();
  const r = restaurants.find((x) => x.id === id);
  const menu = menus[id] || { categories: [], items: [] };
  const [tab, setTab] = useState('menu');
  const [cat, setCat] = useState(menu.categories[0] || '');
  const [ocasion, setOcasion] = useState('all');
  const [product, setProduct] = useState(null);
  const mode = checkout.orderMode === 'pickup' ? 'pickup' : 'delivery';
  const pricing = pricingReady ? deliveryPrice(r?.deliveryFee, selectedAddress(addresses, checkout.addressId), coverage, deliveryPricing) : null;
  React.useEffect(() => {
    if (!r) return;
    if (mode === 'pickup' && !r.pickup) setCheckout((c) => ({ ...c, orderMode: 'delivery' }));
    if (mode === 'delivery' && r.delivery === false && r.pickup) setCheckout((c) => ({ ...c, orderMode: 'pickup' }));
  }, [r, mode, setCheckout]);
  if (!r) return <Screen><TopBar title="Restaurante" /><p className="text-center text-gray-400 mt-10">No encontrado</p></Screen>;
  const fav = favorites.includes(r.id);
  const isGift = r.type === 'regalos';
  const items = menu.items.filter((i) => i.active && (!cat || i.category === cat) && (!isGift || ocasion === 'all' || (i.ocasion || []).includes(ocasion)));
  const add = (item) => {
    if (cart.restaurantId && cart.restaurantId !== r.id) toast('Se reemplazó el carrito con productos de este restaurante');
    addToCart(r.id, item);
    toast.success(`${item.name} agregado`);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 relative">
      <div className="flex-1 overflow-y-auto no-scrollbar pb-24">
        <div className="relative h-[190px]">
          <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow"><ChevronLeft size={22} /></button>
            <div className="flex gap-2">
              <button data-testid="detail-fav" onClick={() => toggleFavorite(r.id)} className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow"><Heart size={18} className={fav ? 'fill-[#EE3B36] text-[#EE3B36]' : ''} /></button>
              <button onClick={() => toast.success('Enlace copiado')} className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow"><Share2 size={18} /></button>
            </div>
          </div>
        </div>
        <div className="px-4 -mt-8 relative">
          <img src={r.logo} alt="" className="w-16 h-16 rounded-2xl object-cover ring-4 ring-white shadow-md" />
          <div className="flex items-start justify-between mt-2">
            <h1 className="text-xl font-extrabold text-[#1E2130]">{r.name}</h1>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${r.open ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}><span className={`w-1.5 h-1.5 rounded-full ${r.open ? 'bg-emerald-500' : 'bg-gray-400'}`} />{r.open ? 'Abierto' : 'Cerrado'}</span>
          </div>
          <p className="text-sm text-gray-700 mt-1 flex items-center gap-1"><Star size={14} className="fill-amber-400 text-amber-400" /> <span className="font-semibold">{r.rating}</span> ({r.reviews} reseñas)</p>
          <p data-testid="restaurant-delivery-fee" className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-1.5"><Clock size={14} /> {r.time} <span className="text-gray-300 mx-1">•</span> Envío {mode === 'pickup' ? 'GRATIS' : pricing ? money(pricing.deliveryFee) : '…'}</p>
          <div className="grid grid-cols-2 gap-2 mt-3" data-testid="service-mode">
            {r.delivery !== false && (
              <button data-testid="mode-delivery" onClick={() => setCheckout({ ...checkout, orderMode: 'delivery' })} className={`rounded-xl border p-2.5 text-left press ${mode === 'delivery' ? 'border-[#EE3B36] bg-[#FDECEC]' : 'border-gray-200 bg-white'}`}>
                <span className="flex items-center gap-1.5 text-[12.5px] font-bold"><Bike size={14} className={mode === 'delivery' ? 'text-[#EE3B36]' : 'text-gray-400'} /> A domicilio</span>
                <span data-testid="mode-delivery-fee" className="block text-[10.5px] text-gray-500 mt-0.5">Envío {pricing ? money(pricing.deliveryFee) : '…'} • {r.time}</span>
              </button>
            )}
            {r.pickup && (
              <button data-testid="mode-pickup" onClick={() => setCheckout({ ...checkout, orderMode: 'pickup' })} className={`rounded-xl border p-2.5 text-left press ${mode === 'pickup' ? 'border-[#2F80ED] bg-[#E8F0FE]' : 'border-gray-200 bg-white'}`}>
                <span className="flex items-center gap-1.5 text-[12.5px] font-bold"><Store size={14} className={mode === 'pickup' ? 'text-[#2F80ED]' : 'text-gray-400'} /> Recoger en tienda</span>
                <span className="block text-[10.5px] text-gray-500 mt-0.5">Sin costo de envío</span>
              </button>
            )}
          </div>
          {!r.pickup && <p className="text-[11px] text-gray-400 mt-1.5">Este negocio solo ofrece entrega a domicilio.</p>}
        </div>

        <div className="flex border-b border-gray-100 mt-4 px-4">
          {[['menu', 'Menú'], ['reviews', 'Reseñas'], ['info', 'Información']].map(([k, l]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === k ? 'border-[#EE3B36] text-[#EE3B36]' : 'border-transparent text-gray-500'}`}>{l}</button>
          ))}
        </div>

        {tab === 'menu' && (
          <>
            {isGift && (
              <div className="mt-3" data-testid="ocasion-filter">
                <p className="px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Ocasión</p>
                <div className="flex gap-2 px-4 mt-2 overflow-x-auto no-scrollbar">
                  <button data-testid="ocasion-all" onClick={() => setOcasion('all')} className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap press ${ocasion === 'all' ? 'bg-[#D6336C] text-white' : 'bg-[#FDECF3] text-[#D6336C]'}`}>Todas</button>
                  {OCASIONES.map((oc) => (
                    <button key={oc.id} data-testid={`ocasion-${oc.id}`} onClick={() => setOcasion(oc.id)} className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap press ${ocasion === oc.id ? 'bg-[#D6336C] text-white' : 'bg-[#FDECF3] text-[#D6336C]'}`}>{oc.label}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 px-4 mt-3 overflow-x-auto no-scrollbar">
              {menu.categories.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${cat === c ? 'bg-[#EE3B36] text-white' : 'bg-gray-100 text-gray-600'}`}>{c}</button>
              ))}
            </div>
            <div className="px-4 mt-3 space-y-3">
              {items.map((i) => (
                <div key={i.id} data-testid={`menu-item-${i.id}`} className="flex items-center gap-3 bg-white" onClick={() => setProduct(i)}>
                  <span className="relative shrink-0">
                    <img src={i.image} alt="" className="w-[72px] h-[72px] rounded-xl object-cover" />
                    {(i.images?.length || 0) > 1 && <span data-testid={`menu-item-photos-${i.id}`} className="absolute bottom-1 right-1 px-1.5 h-4 rounded-full bg-black/60 text-white text-[9px] font-bold flex items-center gap-0.5"><Images size={9} /> {i.images.length}</span>}
                  </span>
                  <div className="flex-1 min-w-0"><p className="font-bold text-[14px]">{i.name}</p><p className="text-xs text-gray-500 truncate">{i.desc}</p><p className="font-bold text-[15px] mt-1">{money(i.price)}{(i.options?.length || i.sizes?.length) ? <span className="ml-2 text-[10px] font-bold text-[#2F80ED] bg-[#E8F0FE] px-1.5 py-0.5 rounded-full align-middle">Elige opciones</span> : null}</p></div>
                  <button data-testid={`add-${i.id}`} onClick={(e) => { e.stopPropagation(); if (i.options?.length || i.sizes?.length) setProduct(i); else add(i); }} className="w-8 h-8 rounded-full bg-[#EE3B36] text-white flex items-center justify-center shadow-md shadow-red-500/30 active:scale-90 transition-transform"><Plus size={18} /></button>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Sin productos en esta categoría</p>}
            </div>
          </>
        )}
        {tab === 'reviews' && (
          <div className="px-4 mt-3 space-y-3">
            {REVIEWS.map((rv) => (
              <div key={rv.name} className="bg-[#F7F8FA] rounded-xl p-3"><div className="flex items-center justify-between"><p className="text-sm font-semibold">{rv.name}</p><span className="text-xs text-gray-400">{rv.time}</span></div><div className="flex gap-0.5 mt-1">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={12} className={s <= rv.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />)}</div><p className="text-sm text-gray-600 mt-1">{rv.text}</p></div>
            ))}
          </div>
        )}
        {tab === 'info' && (
          <div className="px-4 mt-3 space-y-3 text-sm">
            <p className="text-gray-600">{r.description}</p>
            <p className="flex items-center gap-2"><MapPin size={16} className="text-[#EE3B36]" /> {r.address}</p>
            <p className="flex items-center gap-2"><Clock size={16} className="text-[#EE3B36]" /> {r.hours}</p>
            <p className="flex items-center gap-2"><Phone size={16} className="text-[#EE3B36]" /> {r.phone}</p>
            <div className="mock-map h-32 rounded-xl relative"><MapPin size={28} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full text-[#EE3B36] fill-[#EE3B36]" /></div>
          </div>
        )}
      </div>

      {cartCount > 0 && cart.restaurantId === r.id && (
        <div className="absolute bottom-5 left-4 right-4 z-30">
          <PrimaryButton data-testid="view-cart" onClick={() => navigate('/app/cart')} className="flex items-center justify-between px-4 shadow-xl"><span className="bg-white/25 rounded-md px-2 py-0.5 text-xs">{cartCount}</span><span>Ver carrito</span><span>{money(cartSubtotal)}</span></PrimaryButton>
        </div>
      )}

      <Drawer open={!!product} onOpenChange={(o) => !o && setProduct(null)}>
        <DrawerContent className="max-w-[390px] mx-auto">
          {product && <ProductSheet product={product} onAdd={(item) => { add(item); setProduct(null); }} />}
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export const Cart = () => {
  const navigate = useNavigate();
  const { cart, setCart, updateQty, clearCart, cartSubtotal, restaurants, checkout, addresses, coverage, deliveryPricing, pricingReady } = useApp();
  const r = restaurants.find((x) => x.id === cart.restaurantId);
  const pickup = checkout.orderMode === 'pickup' && !!r?.pickup;
  const pricing = pricingReady ? deliveryPrice(r?.deliveryFee, selectedAddress(addresses, checkout.addressId), coverage, deliveryPricing, pickup) : null;
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Screen className="pb-32">
        <TopBar title="Tu carrito" right={<button data-testid="clear-cart" onClick={() => { clearCart(); toast('Carrito vaciado'); }} className="p-2"><Trash2 size={20} /></button>} />
        {cart.items.length === 0 ? (
          <div className="text-center py-20 text-gray-400 px-8"><ShoppingCart size={44} className="mx-auto mb-3" /><p className="font-semibold text-[#1E2130]">Tu carrito está vacío</p><p className="text-sm mt-1">Explora los restaurantes y agrega tus productos favoritos.</p><PrimaryButton className="mt-6" onClick={() => navigate('/app/home')}>Explorar</PrimaryButton></div>
        ) : (
          <div className="px-4">
            {r && <p className="text-xs text-gray-500 mb-2">Pedido de <span className="font-semibold text-[#1E2130]">{r.name}</span></p>}
            {pickup && (
              <div data-testid="cart-pickup-note" className="flex items-center gap-2.5 rounded-xl bg-[#E8F0FE] border border-[#2F80ED]/25 p-3 mb-3">
                <span className="w-9 h-9 rounded-xl bg-white text-[#2F80ED] flex items-center justify-center shrink-0"><Store size={18} /></span>
                <div className="min-w-0"><p className="text-[13px] font-bold">Recoger en tienda</p><p className="text-[11px] text-gray-600 truncate">{r?.address}</p></div>
              </div>
            )}
            <div className="space-y-4">
              {cart.items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex items-center gap-3">
                  <img src={i.image} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1"><p className="font-semibold text-[14px]">{i.name}</p><p className="font-bold text-[15px] mt-1">{money(i.price)}</p></div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`dec-${i.id}`} onClick={() => updateQty(i.id, -1)} className="w-8 h-8 rounded-lg bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center"><Minus size={16} /></button>
                    <span className="w-5 text-center font-bold">{i.qty}</span>
                    <button data-testid={`inc-${i.id}`} onClick={() => updateQty(i.id, 1)} className="w-8 h-8 rounded-lg bg-[#EE3B36] text-white flex items-center justify-center"><Plus size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <p className="text-sm font-semibold mb-2">Instrucciones (opcional)</p>
              <textarea data-testid="cart-notes" value={cart.notes} onChange={(e) => setCart({ ...cart, notes: e.target.value })} placeholder="Ej. Sin cebolla, extra salsa..." rows={2} className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-[#EE3B36] resize-none" />
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {!pickup && <button data-testid="cart-change-address" onClick={() => navigate('/app/address')} className="text-left text-xs text-[#2F80ED] font-semibold break-words">Entrega: {selectedAddress(addresses, checkout.addressId)?.detail || 'Seleccionar dirección'}</button>}
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span className="font-semibold text-[#1E2130]">{money(cartSubtotal)}</span></div>
              <DeliveryFeeBreakdown pricing={pricing} testId="cart-fee" />
              <div className="flex justify-between text-lg font-extrabold border-t pt-3"><span>Total</span><span data-testid="cart-total">{pricing ? money(roundMoney(cartSubtotal + pricing.deliveryFee)) : '…'}</span></div>
            </div>
          </div>
        )}
      </Screen>
      {cart.items.length > 0 && <div className="absolute bottom-0 left-0 right-0 p-4 pb-6 bg-white border-t border-gray-100"><PrimaryButton data-testid="cart-continue" onClick={() => navigate(pickup ? '/app/payment' : '/app/address')}>Continuar</PrimaryButton></div>}
    </div>
  );
};
